/** @file parseTreeSearch: the tree search grammar (`g:3`, `l:`, `r:`, `d:`, `s:`, words) as terms. */

/** One parsed term; every term must match (AND). `text` of a flag term narrows it by substring ('' = no narrowing). */
export type SearchTerm =
  | { kind: 'text'; text: string }
  | { kind: 'group'; group: number }
  | { kind: 'side'; side: 'l' | 'r'; text: string }
  | { kind: 'drawable'; text: string }
  | { kind: 'socket'; text: string };

/** The terms of a search box string (empty list = no filter). */
export type TreeSearch = SearchTerm[];

const FLAG = /^([lrds]):(.*)$/i;
const GROUP = /^g:([1-7])$/i;

const FLAGS: Record<string, (text: string) => SearchTerm> = {
  l: (text) => ({ kind: 'side', side: 'l', text }),
  r: (text) => ({ kind: 'side', side: 'r', text }),
  d: (text) => ({ kind: 'drawable', text }),
  s: (text) => ({ kind: 'socket', text }),
};

function parseTerm(word: string): SearchTerm {
  const group = GROUP.exec(word)?.[1];
  if (group !== undefined) return { kind: 'group', group: Number(group) };
  const flag = FLAG.exec(word);
  const make = FLAGS[flag?.[1]?.toLowerCase() ?? ''];
  return make === undefined ? { kind: 'text', text: word.toLowerCase() } : make((flag?.[2] ?? '').toLowerCase());
}

/**
 * Splits on whitespace: `g:1`..`g:7` group, `l:` / `r:` mirror side, `d:` has drawables, `s:` is a socket target (the
 * last three take an optional substring after the colon, e.g. `d:face`), anything else a case-insensitive substring
 * of the node name. A malformed flag (`g:9`) is a plain word.
 */
export function parseTreeSearch(query: string): TreeSearch {
  return query.split(/\s+/).filter((w) => w !== '').map(parseTerm);
}
